import React, { useEffect, useRef, useState } from "react";
import wordpressIcon from "../../../../assets/images/wordpress.svg";
import hubspotIcon from "../../../../assets/images/hubspot.svg";
import drupalIcon from "../../../../assets/images/drupalIcon.svg";
import geocoderIcon from "../../../../assets/images/geocoderIcon.svg";
import rocketIcon from "../../../../assets/images/rocketIcon.svg";
import infoIcon from "../../../../assets/images/infoIcon.svg";
import uploadIcon from "../../../../assets/images/uploadIcon.svg";
import cloudIcon from "../../../../assets/images/cloudIcon.svg";
import primaryIcon from "../../../../assets/images/primaryIcon.svg";
import linkIcon from "../../../../assets/images/linkIcon.svg";
import listIcon from "../../../../assets/images/listIcon.svg";
import diskIcon from "../../../../assets/images/diskIcon.svg";
import gearsIcon from "../../../../assets/images/gearsIcon.svg";
import trashIconRed from "../../../../assets/images/trashIcon-red.svg";
import trashIcon from "../../../../assets/images/trashIcon.svg";
import iconXML from "../../../../assets/images/iconXML.svg";
import loader from "../../../../assets/images/loader.gif";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  deletePrimaryImage,
  postConfirmSplitFile,
  postPrimaryImage,
  postProject,
  postSeedPageFile,
  postTestProject,
  cancelProject,
} from "../../../../utils/api";
import { useAuth } from "../../../../hooks/useAuth";
import Fancybox from "../Fancybox";
import { useNavigate } from "react-router-dom";
import Modal from "../../../../components/Modal";
import ConfirmModal from "../../../../components/ConfirmModal";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import wix from "../../../../assets/images/wixcom-logo.png";

const projectTypes = {
  Geocoder: geocoderIcon,
  WordPress: wordpressIcon,
  HubSpot: hubspotIcon,
  Drupal: drupalIcon,
  Wix: wix,
};

// Static Yup schema — only field-level rules that apply to every project type.
// The conditional "spreadsheet OR script required" check lives in Formik's
// validate prop (further below) so it has live access to values.selectedProjectType.
const validationSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  notes: Yup.string(),
  google_spread_sheet: Yup.string().url("Enter a valid URL"),
  script: Yup.string(),
  footer_links: Yup.string(),
  uploadedSeedPage: Yup.mixed().nullable(),
});

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 Bytes";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const INITIAL_VALUES = {
  selectedProjectType: "WordPress",
  title: "",
  notes: "",
  site_base_url: "",
  domain: "",
  uploadedImage: null,
  google_spread_sheet: "",
  script: "",
  footer_links:
    "<a href='/geourl' title='INSERT SERVICE Geo City'>Geo City</a><br>",
  uploadedSeedPage: null,
};

const CreateProjectForm = () => {
  const { accessToken } = useAuth();
  const navigate = useNavigate();

  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadedPrimaryImages, setUploadedPrimaryImages] = useState([]);
  const [selectedProjectType, setSelectedProjectType] = useState(
    () =>
      JSON.parse(localStorage.getItem("selectedProjectType")) || {
        name: "WordPress",
      },
  );
  const [uploading, setUploading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPrimaryImage, setUploadingPrimaryImage] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [formData, setFormData] = useState(null);
  const [seedPageValue, setSeedPageValue] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [fileError, setFileError] = useState(null);
  // loadingAction is set the instant a button is clicked — before Formik even
  // starts validation. Buttons show a spinner immediately every time, regardless
  // of whether validation passes or fails.
  // "" = idle | "save" = saving | "test" = testing
  const [loadingAction, setLoadingAction] = useState("");
  // Stores the full backend error response so the debug panel can show it
  const [lastApiError, setLastApiError] = useState(null);

  // submitTypeRef is the single source of truth for which button was pressed.
  // Both buttons are type="button" and call submitForm() directly after setting
  // this ref — there is zero async gap between the click and submission.
  const submitTypeRef = useRef("");

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (accessToken) {
        cancelProject(accessToken).catch((err) =>
          console.error("Error canceling project:", err),
        );
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // DO NOT call cancelProject on unmount — the backend implementation
      // was invalidating the auth token, logging users out on every error
      // or component re-render. Only call it on intentional page unload above.
    };
  }, [accessToken]);

  // ─── File Handlers ────────────────────────────────────────────────────────

  const isValidFileType = (file) => {
    if (selectedProjectType?.name === "Wix") {
      return (
        file.type === "text/csv" ||
        file.name.split(".").pop().toLowerCase() === "csv"
      );
    }
    return (
      file.type === "text/xml" ||
      file.name.split(".").pop().toLowerCase() === "xml"
    );
  };

  const uploadSeedFile = async (file, setFieldValue) => {
    setUploading(true);
    try {
      const response = await postSeedPageFile(accessToken, {
        file,
        type: selectedProjectType?.name,
      });
      setFieldValue("uploadedSeedPage", file);
      setSeedPageValue(file.name);
      setUploadedImages(response.data.data);
      setUploadedPrimaryImages(response.data.data);
    } catch (error) {
      console.error("Error uploading seed file:", error);
      setFileError("Error uploading file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const uploadImages = async (files) => {
    const imageFiles = Array.from(files).filter(
      (f) => f.type.startsWith("image/") && f.size <= 5 * 1024 * 1024,
    );
    if (!imageFiles.length) return;

    setUploadingPrimaryImage(true);
    try {
      const responses = await Promise.all(
        imageFiles.map((f) =>
          postPrimaryImage(accessToken, {
            file: f,
            type: selectedProjectType.name,
          }),
        ),
      );
      const newImages = responses.flatMap((r) => r.data.data);
      setUploadedImages(newImages);
      setUploadedPrimaryImages(newImages);
    } catch (error) {
      console.error("Error uploading images:", error);
    } finally {
      setUploadingPrimaryImage(false);
    }
  };

  const handleFileSelect = (event, setFieldValue, type) => {
    if (type === "file") {
      const file = event.target.files[0];
      if (!file) return;
      if (!isValidFileType(file)) {
        setFileError(
          selectedProjectType?.name === "Wix"
            ? "Only CSV files are allowed for Wix projects"
            : "Only XML files are allowed",
        );
        return;
      }
      setFileError(null);
      uploadSeedFile(file, setFieldValue);
    } else {
      uploadImages(event.target.files);
    }
  };

  const handleFileDrop = (e, setFieldValue, type = "file") => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (!files?.length) return;

    if (type === "file") {
      if (!isValidFileType(files[0])) {
        setFileError(
          selectedProjectType?.name === "Wix"
            ? "Only CSV files are allowed for Wix projects"
            : "Only XML files are allowed",
        );
        return;
      }
      setFileError(null);
      uploadSeedFile(files[0], setFieldValue);
    } else {
      uploadImages(files);
    }
  };

  const handleDeletePrimaryImage = (url) => {
    deletePrimaryImage(
      accessToken,
      url,
      selectedProjectType?.name.toLowerCase(),
    ).then(() => {
      setUploadedPrimaryImages((prev) => prev.filter((f) => f.url !== url));
    });
  };

  const handleProjectTypeChange = (type, resetForm) => {
    setSelectedProjectType(type);
    localStorage.setItem("selectedProjectType", JSON.stringify(type));
    setUploadedImages([]);
    setUploadedPrimaryImages([]);
    setSeedPageValue(null);
    setFileError(null);
    resetForm({
      values: { ...INITIAL_VALUES, selectedProjectType: type.name },
    });
  };

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async (values, { setSubmitting }) => {
    const submitType = submitTypeRef.current;
    console.log("[handleSubmit] type:", submitType, "values:", values);

    if (!submitType) {
      console.error(
        "[handleSubmit] submitTypeRef is empty — this should never happen",
      );
      setSubmitting(false);
      return;
    }

    try {
      if (submitType === "save") {
        const res = await postProject(accessToken, values);
        const isGeocoder =
          values.selectedProjectType?.toLowerCase() === "geocoder";

        if (isGeocoder) {
          // Geocoder returns { success: true, message: "Files has been saved successfully" }
          // with no project_id — navigate to projects list
          setIsError(false);
          setModalMessage("Project was successfully added!");
          setIsModalOpen(true);
          setTimeout(() => navigate("/projects"), 4000);
        } else if (res.data.data.question_is_split_file) {
          setFormData(values);
          setProjectId(res.data.data.project_id);
          setIsConfirmModalOpen(true);
        } else {
          setIsError(false);
          setModalMessage("Project was successfully added!");
          setIsModalOpen(true);
          setTimeout(
            () => navigate(`/projects/${res.data.data.project_id}`),
            4000,
          );
        }
      } else if (submitType === "test") {
        const testPayload = {
          project_id: projectId,
          type: values.selectedProjectType.toLowerCase(),
          title: values.title,
          notes: values.notes || "",
          google_spread_sheet: values.google_spread_sheet || "",
          script: values.script || "",
          footer_links: values.footer_links || "",
          domain_id: values.domain || "",
        };
        console.log("[handleSubmit] testPayload:", testPayload);
        const res = await postTestProject(accessToken, testPayload);
        setIsError(false);
        setModalMessage("Test was successfully started!");
        setIsModalOpen(true);
        setTimeout(() => {
          const id = projectId || res.data?.data?.project_id;
          navigate(id ? `/projects/${id}` : "/projects");
        }, 2000);
      }
    } catch (err) {
      const errData = err.response?.data;
      console.error("[handleSubmit] error status:", err.response?.status);
      console.error(
        "[handleSubmit] error body:",
        JSON.stringify(errData, null, 2),
      );
      setLastApiError({
        status: err.response?.status,
        message: errData?.message || err.message,
        errors: errData?.errors || null,
        raw: errData,
      });
      setModalMessage(errData?.message || "An error occurred");
      setIsError(true);
      setIsModalOpen(true);
    } finally {
      setSubmitting(false);
      setLoadingAction("");
    }
  };

  const handleConfirm = async (splitValues) => {
    setConfirmLoading(true);
    try {
      const res = await postConfirmSplitFile(accessToken, {
        ...splitValues,
        ...formData,
      });
      setIsConfirmModalOpen(false);
      setIsError(false);
      setModalMessage("Project was successfully added!");
      setIsModalOpen(true);
      setTimeout(() => navigate(`/projects/${res.data.data.project_id}`), 4000);
    } catch (err) {
      setIsConfirmModalOpen(false);
      setModalMessage(err.response.data.message);
      setIsError(true);
      setIsModalOpen(true);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleCancel = () => {
    cancelProject(accessToken)
      .catch((err) => console.error("Error canceling project:", err))
      .finally(() => navigate("/projects"));
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Formik
      initialValues={{
        ...INITIAL_VALUES,
        selectedProjectType: selectedProjectType?.name || "WordPress",
      }}
      validationSchema={validationSchema}
      validate={(values) => {
        const errors = {};
        const isGeocoder = values.selectedProjectType === "Geocoder";

        // Non-Geocoder types require a seed page file to be uploaded first.
        // The backend's save-xml reads template.xml from disk — if /upload
        // was never called there is nothing on disk and the save will always fail.
        if (!isGeocoder && !values.uploadedSeedPage) {
          errors.uploadedSeedPage =
            "Please upload a seed page file before saving";
        }

        // Spreadsheet data is required for all types except Geocoder
        if (!isGeocoder && !values.google_spread_sheet && !values.script) {
          errors.google_spread_sheet =
            "Either Google Sheet Link or Manual Spreadsheet Data is required";
        }

        return errors;
      }}
      onSubmit={handleSubmit}
    >
      {({
        values,
        setFieldValue,
        isSubmitting,
        resetForm,
        submitForm,
        errors,
        touched,
        isValidating,
      }) => (
        <Form className="bg-[#F7F7F7] calc-h-header createProjectForm">
          {/* ── Header ── */}
          <div className="p-8">
            <div className="flex gap-[15px] lg:gap-0 flex-col lg:flex-row lg:items-center justify-between">
              <div>
                <div className="flex items-center">
                  <h1 className="text-2xl font-bold">Create Project</h1>
                  <div className="flex items-center ml-[12px]">
                    <img
                      src={projectTypes[values.selectedProjectType]}
                      alt={`${values.selectedProjectType} Icon`}
                      className="w-10 h-10 mr-3"
                    />
                  </div>
                </div>
                <p className="text-[14px] text-[#747474]">
                  Enter your project information below
                </p>
              </div>

              {/* Project type switcher */}
              <div className="border border-[#E0E0E0] rounded-[10px] bg-white p-[15px] flex items-center w-fit">
                <div>
                  <h4 className="text-[14px] text-black font-bold">
                    Project Type
                  </h4>
                  <p className="mb-0 text-[10px] text-[#8C8C8C]">
                    Switch between project types
                  </p>
                </div>
                <div className="flex items-center space-x-4 ml-[14px]">
                  {Object.keys(projectTypes).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        handleProjectTypeChange(
                          {
                            id: Object.keys(projectTypes).indexOf(type) + 1,
                            name: type,
                            logo: projectTypes[type],
                          },
                          resetForm,
                        );
                        setFieldValue("selectedProjectType", type);
                      }}
                      className={`px-4 py-2 border rounded-lg w-[85px] h-[50px] ${
                        values.selectedProjectType === type
                          ? "border-orange-500"
                          : "border-gray-300"
                      }`}
                    >
                      <img
                        src={projectTypes[type]}
                        alt={`${type} Icon`}
                        className="inline"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Body ── */}
            <div className="flex mt-[35px] flex-col lg:flex-row">
              <div className="w-full lg:w-[70%]">
                {/* Project Details */}
                <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] lg:mr-[35px] gap-[22px]">
                  <div className="flex items-center mb-[22px]">
                    <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                      <img
                        src={rocketIcon}
                        alt="Detail Icon"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <h2 className="text-[18px] text-black font-semibold">
                        Project Details
                      </h2>
                      <p className="text-[12px] text-[#8C8C8C]">
                        Name your project and add any additional information for
                        internal purposes.
                      </p>
                    </div>
                  </div>

                  <div className="mb-[22px]">
                    <label
                      htmlFor="title"
                      className="flex text-[12px] font-[600] text-[#001522]"
                    >
                      Title
                      <Tooltip tooltip="This will be the title of your project">
                        <img src={infoIcon} alt="Info" className="ml-1" />
                      </Tooltip>
                    </label>
                    <Field
                      type="text"
                      id="title"
                      name="title"
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    />
                    <ErrorMessage
                      name="title"
                      component="div"
                      className="text-red-500 text-xs"
                    />
                  </div>

                  {values.selectedProjectType === "Drupal" && (
                    <div className="mb-[22px]">
                      <label
                        htmlFor="site-base-url"
                        className="flex text-[12px] font-[600] text-[#001522]"
                      >
                        Site base URL
                        <Tooltip tooltip="This will be the Site base URL of your project">
                          <img src={infoIcon} alt="Info" className="ml-1" />
                        </Tooltip>
                      </label>
                      <Field
                        type="text"
                        id="site-base-url"
                        name="site-base-url"
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      />
                      <ErrorMessage
                        name="site-base-url"
                        component="div"
                        className="text-red-500 text-xs"
                      />
                    </div>
                  )}

                  {values.selectedProjectType !== "Geocoder" && (
                    <div className="w-full">
                      <label
                        htmlFor="notes"
                        className="flex text-[12px] font-[600] text-[#001522]"
                      >
                        Notes
                        <Tooltip tooltip="Name and add any notes you have regarding your project below">
                          <img src={infoIcon} alt="Info" className="ml-1" />
                        </Tooltip>
                      </label>
                      <Field
                        as="textarea"
                        id="notes"
                        name="notes"
                        rows={4}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      />
                      <ErrorMessage
                        name="notes"
                        component="div"
                        className="text-red-500 text-xs"
                      />
                    </div>
                  )}
                </div>

                {/* Upload Images (Geocoder only) */}
                {values.selectedProjectType === "Geocoder" && (
                  <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] lg:mr-[35px] mt-[30px]">
                    <div className="flex items-center mb-[22px]">
                      <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                        <img
                          src={uploadIcon}
                          alt="Upload Icon"
                          className="w-full"
                        />
                      </div>
                      <div>
                        <h2 className="text-[18px] text-black font-semibold flex items-center">
                          Upload Images
                          <Tooltip tooltip="Click or drag and drop to upload your Image file">
                            <img src={infoIcon} alt="Info" className="ml-1" />
                          </Tooltip>
                        </h2>
                        <p className="text-[12px] text-[#8C8C8C]">
                          Click or drag and drop to upload your Image file.
                        </p>
                      </div>
                    </div>

                    <div
                      className={`border-dashed border-2 border-gray-300 p-4 text-center flex flex-col ${
                        values.uploadedImage ? "items-start" : "items-center"
                      } rounded-[6px]`}
                      onDrop={(e) => handleFileDrop(e, setFieldValue, "image")}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      {uploading ? (
                        <div>Loading...</div>
                      ) : values.uploadedImage ? (
                        <div className="flex items-center">
                          <div className="text-center">
                            <img src={iconXML} alt="icon XML" />
                            <span className="text-[#8C8C8C] text-[10px] font-[600]">
                              {formatFileSize(values.uploadedImage?.size)}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setFieldValue("uploadedImage", null)}
                            className="flex text-[#E80000] border border-[#E80000] text-[10px] font-[600] rounded-[5px] px-[13px] py-[8px] ml-[35px]"
                          >
                            <img src={trashIconRed} alt="Trash Icon" />
                            Remove Image
                          </button>
                        </div>
                      ) : (
                        <>
                          <img
                            src={cloudIcon}
                            alt="Cloud"
                            className="max-w-[31px] mb-2"
                          />
                          <p className="text-[13px] text-black font-bold">
                            Drag and Drop image here
                          </p>
                          <p className="text-[10px] text-[#8C8C8C]">or</p>
                          <input
                            type="file"
                            id="fileInputGeo"
                            multiple
                            className="hidden"
                            onChange={(e) =>
                              handleFileSelect(e, setFieldValue, "image")
                            }
                          />
                          <label
                            htmlFor="fileInputGeo"
                            className="inline-block mt-2 px-[8px] py-[3px] bg-gradient text-white text-[8px] font-semibold rounded-[5px] cursor-pointer"
                          >
                            Browse Files
                          </label>
                          <p className="text-[7px] text-[#8C8C8C] font-[400] mt-2">
                            Max file size: 5 MB per image
                          </p>
                          <Fancybox options={{ Carousel: { infinite: false } }}>
                            {uploadedImages?.map((file, index) => (
                              <div
                                className="flex flex-col justify-center w-[46%]"
                                key={index}
                              >
                                <div className="primaryImage">
                                  <img
                                    src={file.url}
                                    alt=""
                                    className="max-w-[154px] max-h-[88px] w-full photo"
                                  />
                                  <div className="wrapper-icons flex justify-center gap-[40px]">
                                    <a
                                      data-fancybox="gallery"
                                      href={file.url}
                                      className="flex items-center flex-col gap-[4px]"
                                    >
                                      <span className="text-white text-[7px]">
                                        View
                                      </span>
                                    </a>
                                    <button
                                      type="button"
                                      className="flex items-center flex-col gap-[4px]"
                                      onClick={() => {
                                        setUploadedImages((prev) =>
                                          prev.filter(
                                            (f) => f.url !== file.url,
                                          ),
                                        );
                                      }}
                                    >
                                      <img src={trashIcon} alt="trash Icon" />
                                      <span className="text-white text-[7px]">
                                        Remove
                                      </span>
                                    </button>
                                  </div>
                                </div>
                                <span className="text-[#6C6D74] text-[8px] max-w-[100px] whitespace-nowrap overflow-hidden overflow-ellipsis pt-[5px] my-0 mx-auto">
                                  {file.fileName}
                                </span>
                              </div>
                            ))}
                            {uploadingImage && (
                              <div className="absolute top-0 left-0 w-full h-full bg-gray-400 opacity-50 flex items-center justify-center rounded-[4px]">
                                <img
                                  src={loader}
                                  alt="Loader"
                                  className="w-[100px] h-[100px]"
                                />
                              </div>
                            )}
                          </Fancybox>
                          <p className="text-[7px] text-[#8C8C8C] font-[400] mt-2">
                            Supports: JPEG
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Upload Seed Page (non-Geocoder) */}
                {values.selectedProjectType !== "Geocoder" && (
                  <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] lg:mr-[35px] mt-[30px]">
                    <div className="flex items-center mb-[22px]">
                      <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                        <img
                          src={uploadIcon}
                          alt="Upload Icon"
                          className="w-full"
                        />
                      </div>
                      <div>
                        <h2 className="text-black font-semibold flex items-center">
                          <span className="text-[18px]">
                            {selectedProjectType?.name === "Wix"
                              ? "Upload Seed Collection"
                              : "Upload Seed Page"}
                          </span>
                          <Tooltip
                            tooltip={
                              selectedProjectType?.name === "Wix"
                                ? "Export the seed collection from Wix and upload it below"
                                : "Export the seed page from your CMS and upload it below"
                            }
                          >
                            <img src={infoIcon} alt="Info" className="ml-1" />
                          </Tooltip>
                        </h2>
                        <p className="text-[12px] text-[#8C8C8C]">
                          {selectedProjectType?.name === "Wix"
                            ? "Click or drag and drop to upload your CSV file."
                            : "Click or drag and drop to upload your XML file."}
                        </p>
                      </div>
                    </div>

                    <div
                      className={`border-dashed border-2 border-gray-300 p-4 text-center flex flex-col ${
                        values.uploadedSeedPage ? "items-start" : "items-center"
                      } rounded-[6px]`}
                      onDrop={(e) => handleFileDrop(e, setFieldValue, "file")}
                      onDragOver={(e) => e.preventDefault()}
                      onDragEnter={(e) => e.preventDefault()}
                    >
                      {fileError && (
                        <div className="text-red-500 text-sm mb-2">
                          {fileError}
                        </div>
                      )}

                      {uploading ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <img src={loader} alt="Loading" className="w-6 h-6" />
                          Uploading...
                        </div>
                      ) : values.uploadedSeedPage ? (
                        <div className="flex items-center">
                          <div className="text-center">
                            <img src={iconXML} alt="icon XML" />
                            <span className="text-[#8C8C8C] text-[10px] font-[600]">
                              {formatFileSize(values.uploadedSeedPage?.size)}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setFieldValue("uploadedSeedPage", null);
                              setSeedPageValue(null);
                            }}
                            className="flex text-[#E80000] border border-[#E80000] text-[10px] font-[600] rounded-[5px] px-[13px] py-[8px] ml-[35px]"
                          >
                            <img src={trashIconRed} alt="Trash Icon" />
                            Remove File
                          </button>
                        </div>
                      ) : (
                        <>
                          <img
                            src={cloudIcon}
                            alt="Cloud"
                            className="max-w-[31px] mb-2"
                          />
                          <p className="text-[13px] text-black font-bold">
                            Drag and Drop file here
                          </p>
                          <p className="text-[10px] text-[#8C8C8C]">or</p>
                          <input
                            type="file"
                            accept={
                              selectedProjectType?.name === "Wix"
                                ? ".csv"
                                : ".xml"
                            }
                            className="hidden"
                            id="selectXML"
                            onChange={(e) =>
                              handleFileSelect(e, setFieldValue, "file")
                            }
                          />
                          <label
                            htmlFor="selectXML"
                            className="inline-block mt-2 px-[8px] py-[3px] bg-gradient text-white text-[8px] font-semibold rounded-[5px] cursor-pointer"
                          >
                            Browse Files
                          </label>
                          <p className="text-[7px] text-[#8C8C8C] font-[400] mt-2">
                            Supports:{" "}
                            {selectedProjectType?.name === "Wix"
                              ? "CSV"
                              : "XML"}
                          </p>
                        </>
                      )}
                    </div>
                    <ErrorMessage
                      name="uploadedSeedPage"
                      component="div"
                      className="text-red-500 text-xs"
                    />
                  </div>
                )}

                {/* Import Spreadsheet */}
                <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] lg:mr-[35px] mt-[30px]">
                  <div className="flex items-center mb-[22px]">
                    <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                      <img
                        src={listIcon}
                        alt="Spreadsheet Icon"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <h2 className="text-[18px] text-black font-semibold flex items-center">
                        Import Spreadsheet Data
                        <Tooltip tooltip="Import Spreadsheet Data">
                          <img src={infoIcon} alt="Info" className="ml-1" />
                        </Tooltip>
                      </h2>
                      <p className="text-[12px] text-[#8C8C8C]">
                        Provide a Google Sheet link or paste data manually.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="google_spread_sheet"
                      className="flex text-[12px] font-[600] text-[#001522]"
                    >
                      Google Sheet Link
                      <Tooltip tooltip="Google Sheet Link">
                        <img src={infoIcon} alt="Info" className="ml-1" />
                      </Tooltip>
                    </label>
                    <Field
                      type="text"
                      id="google_spread_sheet"
                      name="google_spread_sheet"
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    />
                    <ErrorMessage
                      name="google_spread_sheet"
                      component="div"
                      className="text-red-500 text-xs"
                    />
                  </div>

                  <div className="flex items-center my-[10px]">
                    <div className="w-[23px] bg-[#6C6D74] h-[1px]" />
                    <span className="mx-[7px] text-[#6C6D74] text-[10px] font-[600]">
                      OR
                    </span>
                    <div className="w-[23px] bg-[#6C6D74] h-[1px]" />
                  </div>

                  <div className="mb-[22px]">
                    <label
                      htmlFor="script"
                      className="flex text-[12px] font-[600] text-[#001522]"
                    >
                      Manually Paste Data From Spreadsheet
                      <Tooltip tooltip="Manually Paste Data From Spreadsheet">
                        <img src={infoIcon} alt="Info" className="ml-1" />
                      </Tooltip>
                    </label>
                    <Field
                      as="textarea"
                      id="script"
                      name="script"
                      rows={4}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    />
                  </div>
                </div>

                {/* Footer Links (non-Geocoder) */}
                {values.selectedProjectType !== "Geocoder" && (
                  <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] lg:mr-[35px] mt-[30px]">
                    <div className="flex items-center mb-[22px]">
                      <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                        <img
                          src={linkIcon}
                          alt="Link Icon"
                          className="w-full"
                        />
                      </div>
                      <div>
                        <h2 className="text-[18px] text-black font-semibold flex items-center">
                          Footer Link Structure
                          <Tooltip tooltip="Footer Link Structure">
                            <img src={infoIcon} alt="Info" className="ml-1" />
                          </Tooltip>
                        </h2>
                        <p className="text-[12px] text-[#8C8C8C]">
                          Specify how you would like your footer links to be
                          structured
                        </p>
                      </div>
                    </div>
                    <div className="mb-[22px]">
                      <label
                        htmlFor="footer_links"
                        className="flex text-[12px] font-[600] text-[#001522]"
                      >
                        Link Structure
                        <Tooltip tooltip="Link Structure">
                          <img src={infoIcon} alt="Info" className="ml-1" />
                        </Tooltip>
                      </label>
                      <Field
                        type="text"
                        id="footer_links"
                        name="footer_links"
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Primary Images sidebar (non-Geocoder) */}
              {values.selectedProjectType !== "Geocoder" && (
                <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] w-full max-w-[375px] mt-[30px] lg:mt-[0] h-fit">
                  <div className="flex items-center mb-[22px]">
                    <div className="p-[13px] bg-gradient max-w-[44px] max-h-[44px] rounded-[6px] mr-[16px]">
                      <img
                        src={primaryIcon}
                        alt="Primary Icon"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <h2 className="text-[18px] text-black font-semibold flex items-center">
                        Primary Images
                        <Tooltip tooltip="Primary Images">
                          <img src={infoIcon} alt="Info" className="ml-1" />
                        </Tooltip>
                      </h2>
                      <p className="text-[12px] text-[#8C8C8C]">
                        Upload additional images for geo processing
                      </p>
                    </div>
                  </div>

                  <div
                    className="border-dashed rounded-[6px] border-2 border-gray-300 p-4 text-center flex flex-col items-center mb-[27px]"
                    onDrop={(e) => handleFileDrop(e, setFieldValue, "image")}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnter={(e) => e.preventDefault()}
                  >
                    <img
                      src={cloudIcon}
                      alt="Cloud"
                      className="max-w-[31px] mb-2"
                    />
                    <p className="text-[13px] text-black font-bold">
                      Drag and Drop files here
                    </p>
                    <p className="text-[10px] text-[#8C8C8C]">or</p>
                    <input
                      type="file"
                      id="fileInput"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) =>
                        handleFileSelect(e, setFieldValue, "image")
                      }
                    />
                    <label
                      htmlFor="fileInput"
                      className="inline-block mt-2 px-[8px] py-[3px] bg-gradient text-white text-[8px] font-semibold rounded-[5px] cursor-pointer"
                    >
                      Browse Files
                    </label>
                    <p className="text-[7px] text-[#8C8C8C] font-[400] mt-2">
                      Max file size: 5 MB per image
                    </p>
                  </div>

                  <Fancybox options={{ Carousel: { infinite: false } }}>
                    {uploadedPrimaryImages?.map((file, index) => (
                      <div
                        className="flex flex-col justify-center w-[46%]"
                        key={index}
                      >
                        <div className="primaryImage">
                          <img
                            src={file.url}
                            alt=""
                            className="max-w-[154px] max-h-[88px] w-full photo"
                          />
                          <div className="wrapper-icons flex justify-center gap-[40px]">
                            <a
                              data-fancybox="gallery"
                              href={file.url}
                              className="flex items-center flex-col gap-[4px]"
                            >
                              <span className="text-white text-[7px]">
                                View
                              </span>
                            </a>
                            <button
                              type="button"
                              className="flex items-center flex-col gap-[4px]"
                              onClick={() => handleDeletePrimaryImage(file.url)}
                            >
                              <img src={trashIcon} alt="trash Icon" />
                              <span className="text-white text-[7px]">
                                Remove
                              </span>
                            </button>
                          </div>
                        </div>
                        <span className="text-[#6C6D74] text-[8px] max-w-[100px] whitespace-nowrap overflow-hidden overflow-ellipsis pt-[5px] my-0 mx-auto">
                          {file.fileName}
                        </span>
                      </div>
                    ))}
                    {uploadingPrimaryImage && (
                      <div className="absolute top-0 left-0 w-full h-full bg-gray-400 opacity-50 flex items-center justify-center rounded-[4px]">
                        <img
                          src={loader}
                          alt="Loader"
                          className="w-[100px] h-[100px]"
                        />
                      </div>
                    )}
                  </Fancybox>
                </div>
              )}
            </div>
          </div>

          {/* ── Sticky footer bar ── */}
          <div className="sticky w-full bottom-0 left-0 flex justify-between bg-white border-t border-t-[#E0E0E0] pt-5 pb-5 px-9">
            <div className="flex gap-3">
              {/*
                BOTH buttons are type="button".
                We set the ref synchronously, then call submitForm() directly.
                submitForm() is Formik's programmatic trigger — it runs validation
                first, then calls onSubmit only if everything passes.
                There is zero async gap. No race condition possible.
              */}
              <button
                type="button"
                disabled={!!loadingAction}
                onClick={async () => {
                  submitTypeRef.current = "save";
                  setLoadingAction("save");
                  await submitForm();
                  // If validation failed, submitForm resolves but onSubmit is
                  // never called, so loadingAction would stay "save" forever.
                  // Clear it here as a safety net — handleSubmit's finally block
                  // will also clear it on success/error.
                  setLoadingAction((prev) => (prev === "save" ? "" : prev));
                }}
                className={`flex items-center flex-wrap bg-gradient-to-r from-[#fe6432] to-[#f8761f] rounded-[5px] text-white text-[10px] font-[600] px-[13px] py-[8px] ${
                  loadingAction ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {loadingAction === "save" ? (
                  <>
                    <img
                      src={loader}
                      alt="loading"
                      className="w-4 h-4 mr-[5px]"
                    />
                    Saving...
                  </>
                ) : (
                  <>
                    <img src={diskIcon} alt="Disk Icon" className="mr-[5px]" />
                    Save Project
                  </>
                )}
              </button>

              <button
                type="button"
                disabled={!!loadingAction}
                onClick={async () => {
                  submitTypeRef.current = "test";
                  setLoadingAction("test");
                  await submitForm();
                  setLoadingAction((prev) => (prev === "test" ? "" : prev));
                }}
                className={`flex items-center text-[#001522] border border-[#000000] text-[10px] font-[600] rounded-[5px] px-[13px] py-[8px] ${
                  loadingAction ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {loadingAction === "test" ? (
                  <>
                    <img
                      src={loader}
                      alt="loading"
                      className="w-4 h-4 mr-[5px]"
                    />
                    Testing...
                  </>
                ) : (
                  <>
                    <img
                      src={gearsIcon}
                      alt="Gears Icon"
                      className="mr-[5px]"
                    />
                    Test Page
                  </>
                )}
              </button>
            </div>

            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center text-[#E80000] border border-[#E80000] text-[10px] font-[600] rounded-[5px] px-[13px] py-[8px]"
            >
              <img src={trashIconRed} alt="Trash Icon" className="mr-[5px]" />
              Cancel
            </button>
          </div>

          {isModalOpen && (
            <Modal
              message={modalMessage}
              isError={isError}
              onClose={() => setIsModalOpen(false)}
            />
          )}

          {isConfirmModalOpen && (
            <ConfirmModal
              message="Your output XML file is larger than 10MB. Would you like us to split it into multiple files for an easier upload?"
              onConfirm={() => handleConfirm({ split_xml_file: 2 })}
              onCancel={() => handleConfirm({ split_xml_file: 0 })}
              loading={confirmLoading}
            />
          )}
        </Form>
      )}
    </Formik>
  );
};

export default CreateProjectForm;
